import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Bot, MessageSquare, TrendingUp, ChevronRight, Sparkles } from "lucide-react";

const CareerTwinWidget = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [twinData, setTwinData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchTwinData();
    }
  }, [token]);

  const fetchTwinData = async () => {
    try {
      const response = await axios.get(`${API}/career-twin/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwinData(response.data);
    } catch (error) {
      console.error("Error fetching twin data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate avatar URL using saved avatar_config or default
  const generateAvatarUrl = (twin) => {
    const config = twin?.avatar_config;
    
    if (!config || typeof config !== 'object' || !config.top) {
      // Return default avatar URL based on twin name
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${twin?.twin_name || 'CareerTwin'}&backgroundColor=b6e3f4`;
    }
    
    const params = new URLSearchParams();
    params.append("seed", twin?.twin_name || "CareerTwin");
    
    // Only append parameters that have values
    if (config.top) params.append("top", config.top);
    if (config.hairColor) params.append("hairColor", config.hairColor);
    if (config.accessories) params.append("accessories", config.accessories);
    if (config.facialHair) params.append("facialHair", config.facialHair);
    if (config.clothing) params.append("clothing", config.clothing);
    if (config.clothesColor) params.append("clothesColor", config.clothesColor);
    if (config.skinColor) params.append("skinColor", config.skinColor);
    if (config.eyes) params.append("eyes", config.eyes);
    if (config.eyebrows) params.append("eyebrows", config.eyebrows);
    if (config.mouth) params.append("mouth", config.mouth);
    if (config.backgroundColor) params.append("backgroundColor", config.backgroundColor);
    
    return `https://api.dicebear.com/7.x/avataaars/svg?${params.toString()}`;
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!twinData) return null;

  const { twin, dna_profile, unread_insights, statistics } = twinData;
  
  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getInsightMessage = () => {
    const score = dna_profile?.dna_strength_score || 0;
    if (score >= 80) {
      return "You're doing excellent! Keep up the great work! 🌟";
    } else if (score >= 60) {
      return "Good progress! Let's boost your score together! 📈";
    } else {
      return "Ready to accelerate your career? I'm here to help! 🚀";
    }
  };

  return (
    <Card className="border-2 border-indigo-100 hover:border-indigo-300 transition-all duration-300 bg-gradient-to-br from-white to-indigo-50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="relative">
              <Bot className="w-6 h-6 text-indigo-600" />
              <Sparkles className="w-3 h-3 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
            </div>
            Your Career Twin
          </CardTitle>
          {unread_insights > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse">
              {unread_insights} New
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Twin Avatar and Name */}
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
            style={{ 
              backgroundColor: twin.avatar_config?.backgroundColor 
                ? `#${twin.avatar_config.backgroundColor}` 
                : '#b6e3f4' 
            }}
          >
            <img 
              src={generateAvatarUrl(twin)}
              alt={twin.twin_name}
              className="w-full h-full"
              data-testid="dashboard-twin-avatar"
            />
          </div>
          <div>
            <p className="font-semibold text-gray-800">{twin.twin_name}</p>
            <p className="text-xs text-gray-500 capitalize">{twin.personality_type} Companion</p>
          </div>
        </div>

        {/* Latest Insight */}
        <div className="bg-white p-3 rounded-lg border border-indigo-100">
          <p className="text-sm text-gray-700 flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
            <span>{getInsightMessage()}</span>
          </p>
        </div>

        {/* Career Score */}
        <div className="bg-white p-3 rounded-lg border border-indigo-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              DNA Strength
            </span>
            <span className={`text-xl font-bold ${getScoreColor(dna_profile?.dna_strength_score || 0)}`}>
              {dna_profile?.dna_strength_score || 0}
            </span>
          </div>
          <Progress value={dna_profile?.dna_strength_score || 0} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">
            {dna_profile?.completion_percentage || 0}% profile complete
          </p>
        </div>

        {/* Quick Stats - DNA Info */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white p-2 rounded-lg border border-indigo-100">
            <p className="text-xs text-gray-500">Courses</p>
            <p className="text-lg font-bold text-indigo-600">{statistics?.total_courses || 0}</p>
          </div>
          <div className="bg-white p-2 rounded-lg border border-green-100">
            <p className="text-xs text-gray-500">Genes</p>
            <p className="text-lg font-bold text-green-600">{dna_profile?.skill_genes_count || 0}</p>
          </div>
          <div className="bg-white p-2 rounded-lg border border-orange-100">
            <p className="text-xs text-gray-500">Mutations</p>
            <p className="text-lg font-bold text-orange-600">{dna_profile?.skill_mutations_count || 0}</p>
          </div>
        </div>

        {/* CTA Button */}
        <Button 
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
          onClick={() => navigate('/career-twin')}
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Chat with {twin.twin_name}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default CareerTwinWidget;
